import { Controller } from "@hotwired/stimulus";

// Cooking mode: a full-screen, one-step-at-a-time view of a recipe for phones
// and tablets propped up on the counter. Every step is rendered server side, so
// this only toggles which one is visible. While the overlay is open it keeps the
// screen awake where the Wake Lock API is available.
export default class extends Controller {
  static targets = ["overlay", "step", "previous", "next"];
  static values = { index: Number };

  connect() {
    this.handleKeydown = this.handleKeydown.bind(this);
    this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
  }

  disconnect() {
    this.stopListening();
    this.releaseWakeLock();
    this.unlockScroll();
  }

  open() {
    this.opener = document.activeElement;
    this.overlayTarget.classList.remove("hidden");
    this.indexValue = 0;
    this.showCurrentStep();
    this.lockScroll();
    this.startListening();
    this.requestWakeLock();
    this.overlayTarget.focus();
  }

  close() {
    this.overlayTarget.classList.add("hidden");
    this.stopListening();
    this.releaseWakeLock();
    this.unlockScroll();

    if (this.opener) this.opener.focus();
  }

  next() {
    if (this.indexValue < this.lastIndex) this.indexValue += 1;
  }

  previous() {
    if (this.indexValue > 0) this.indexValue -= 1;
  }

  indexValueChanged() {
    this.showCurrentStep();
  }

  showCurrentStep() {
    if (!this.hasNextTarget) return;

    this.stepTargets.forEach((step, index) => {
      step.classList.toggle("hidden", index !== this.indexValue);
    });

    this.previousTarget.disabled = this.indexValue === 0;
    this.nextTarget.disabled = this.indexValue === this.lastIndex;
  }

  get lastIndex() {
    return this.stepTargets.length - 1;
  }

  get isOpen() {
    return !this.overlayTarget.classList.contains("hidden");
  }

  // Navigation ---------------------------------------------------------------

  handleKeydown(event) {
    if (event.key === "Escape") {
      this.close();
    } else if (event.key === "ArrowRight" || event.key === " ") {
      event.preventDefault();
      this.next();
    } else if (event.key === "ArrowLeft") {
      event.preventDefault();
      this.previous();
    }
  }

  startListening() {
    document.addEventListener("keydown", this.handleKeydown);
    document.addEventListener("visibilitychange", this.handleVisibilityChange);
  }

  stopListening() {
    document.removeEventListener("keydown", this.handleKeydown);
    document.removeEventListener(
      "visibilitychange",
      this.handleVisibilityChange,
    );
  }

  // Screen wake lock ---------------------------------------------------------

  async requestWakeLock() {
    if (!("wakeLock" in navigator)) return;

    try {
      this.wakeLock = await navigator.wakeLock.request("screen");
    } catch {
      // Denied or unsupported: cooking mode still works, the screen just dims.
    }
  }

  releaseWakeLock() {
    if (!this.wakeLock) return;

    this.wakeLock.release();
    this.wakeLock = null;
  }

  // The browser drops the lock whenever the tab is hidden, so take it back once
  // the cook returns to the page.
  handleVisibilityChange() {
    if (document.visibilityState === "visible" && this.isOpen)
      this.requestWakeLock();
  }

  // Scrolling ----------------------------------------------------------------

  lockScroll() {
    document.body.classList.add("overflow-hidden");
  }

  unlockScroll() {
    document.body.classList.remove("overflow-hidden");
  }
}
